import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import KeyboardBackspaceOutlinedIcon from '@mui/icons-material/KeyboardBackspaceOutlined';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import Logo from '@/Asset/images/logo-blaxk.webp';
import LoaderComponet from '../LoderComponent';
import { buildRoutes } from '@/service/buildRoutes';
import Toast from '@/utils/toastManager';
import './ForgotPassword.css';

function RestPassword() {
	const [password, setPassword] = useState('');
	const [loading, setLoading] = useState(false);
	const [responseMessage, setResponseMessage] = useState('');
	const [showPassword, setShowPassword] = useState(false);
	const navigate = useNavigate();
	const { id, token } = useParams();

	axios.defaults.withCredentials = true;

	const handleLogoClick = () => navigate(buildRoutes.toLogin());

	const handleSubmit = e => {
		e.preventDefault();
		setLoading(true);
		setResponseMessage('');

		axios
			.post(`${import.meta.env.VITE_APP_API_URL}/reset-password/${id}/${token}`, { password })
			.then(res => {
				if (res.data.Status === 'Success') {
					Toast.success('Password reset successful');
					navigate(buildRoutes.toLogin());
					return;
				}

				setResponseMessage(res.data.Status || 'Unable to reset password');
			})
			.catch(err => {
				setResponseMessage(err.response?.data?.message || 'Something went wrong');
			})
			.finally(() => {
				setLoading(false);
			});
	};

	return (
		<>
			<Helmet>
				<title>Create New Password | The Godfrey Method App</title>
				<meta name="description" content="Create a new password for your account" />
			</Helmet>

			<div className="forgot-password-container">
				<div className="onboarding-create-profile__brand-row">
					<button
						type="button"
						className="onboarding-create-profile__back-btn"
						onClick={handleLogoClick}
					>
						<KeyboardBackspaceOutlinedIcon />
					</button>
					<img src={Logo} alt="The Godfrey Method On Demand" className="onboarding-create-profile__logo" />
				</div>

				<div className="forgot-password-box">
					<h1 className="forgot-password-title">RESET PASSWORD</h1>
					<p className="forgot-password-subtitle">
						Create a new password below to finish resetting your account.
					</p>

					<form onSubmit={handleSubmit} className="forgot-password-form">
						<div className="forgot-password-form-group">
							<label className="forgot-password-label">NEW PASSWORD</label>
							<div className="forgot-password-input-with-icon">
								<span className="forgot-password-input-icon" aria-hidden="true">
									<svg width="20" height="20" viewBox="0 0 24 24" fill="none">
										<path
											d="M8 10V7a4 4 0 1 1 8 0v3"
											stroke="currentColor"
											strokeWidth="1.8"
											strokeLinecap="round"
											strokeLinejoin="round"
										/>
										<rect
											x="5"
											y="10"
											width="14"
											height="10"
											rx="2"
											stroke="currentColor"
											strokeWidth="1.8"
										/>
									</svg>
								</span>
								<input
									type={showPassword ? 'text' : 'password'}
									name="password"
									className="forgot-password-input forgot-password-input--with-icon"
									value={password}
									onChange={e => setPassword(e.target.value)}
									placeholder="••••••••••"
									autoComplete="new-password"
									required
								/>
								<button
									type="button"
									className="forgot-password-password-toggle"
									onClick={() => setShowPassword(prev => !prev)}
									aria-label={showPassword ? 'Hide password' : 'Show password'}
								>
									{showPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
								</button>
							</div>
						</div>

						{responseMessage && <div className="forgot-password-error">{responseMessage}</div>}

						<button type="submit" className="forgot-password-button" disabled={loading}>
							UPDATE PASSWORD
							<svg width="20" height="20" viewBox="0 0 20 20" fill="none">
								<path d="M4 10h12m0 0l-4-4m4 4l-4 4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
							</svg>
						</button>
					</form>
				</div>
			</div>

			{loading && <LoaderComponet loading />}
		</>
	);
}

export default RestPassword;
